import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { getDateFromOffset } from '../../../helpers/dates';
import { WelletContext } from '../../../context/WelletContext';
import { skeletonLoading } from '../../../components/Skeletons/SkeletonAccount';
import { useTranslation } from 'react-i18next';
import ContentApp from '../../../components/ContentApp';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import AccountHeader from '../../../components/AccountHeader/AccountHeader';
import useRealtimeReservations from '../../../hooks/useRealtimeReservations';
import { CircleSpinner } from 'react-spinners-kit';
import CustomButton from '../../../components/ButtomCustom/CustomButton';
import ArrowIcon from '../../../components/icons/ArrowIcon';
import InstagramIcon2 from '../../../components/icons/InstagramIcon2';
import PaymentLink from '../../../components/icons/PaymentLink';
import LeadItem from '../../../components/LeadItem/LeadItem';
import QrIconHome from '../../../components/icons/QrIconHome';
import Button from '../../../components/Common/Button/Button';
import Balance from '../../../components/Balance/Balance';
import WarningBox from '../../../components/Common/WarningBox/WarningBox';
import NavSelector from '../../../components/NavSelector/NavSelector';

const Home = ({ history }) => {
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const jwt = useSelector((state) => state.associates.jwt);
  const user = useSelector((state) => state.associates.user);
  const userWithdrawalMethod = user?.userWithdrawalMethod;
  const today = getDateFromOffset(user.companyUtcMinutesOffset);
  const [monthEarning, setMonthEarning] = useState(0);
  const todayFilter = format(today, 'yyyy-MM-dd');
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [filterDate, setFilterDate] = useState(
    localStorage.getItem('filterPaymentLink') ?? 'CREATED_DATE',
  );

  const [myEarningsToday, setMyEarningsToday] = useState(null);
  const [selectedOption, setSelectedOption] = useState(filterDate);
  const app = useSelector((state) => state.app);
  const kycStatus = useSelector((state) => state.associates.user.kycStatus);
  const advantageNetworkStatus = useSelector(
    (state) => state?.associates?.user?.advantageNetworkStatus,
  );
  const wireMethod =
    user.withdrawalMethod === 'WIRE_INTERNATIONAL' || user.withdrawalMethod === 'WIRE_MEXICO';

  const getMyEarningsToday = async () => {
    setLoadingBalance(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetEarnings?from=${todayFilter}&to=${todayFilter}`,
      );
      setMyEarningsToday(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const getEarnings = async (fromDate, toDate) => {
    setLoadingBalance(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetEarnings?from=${fromDate}&to=${toDate}`,
      );
      setMonthEarning(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleValidReservationUpdate = useCallback(() => {
    getMyEarningsToday();

    const date = getDateFromOffset(user.companyUtcMinutesOffset);
    const firstMonthDate = format(startOfMonth(date), 'yyyy-MM-dd');
    const lastMonthDate = format(endOfMonth(date), 'yyyy-MM-dd');

    getEarnings(firstMonthDate, lastMonthDate);
  }, [getMyEarningsToday, getEarnings]);

  const reservationTypes = {
    ReservationTypeInclude: ['LEAD'],
    ReservationTypeExclude: [],
  };

  const { reservations, loading, hasMore, loadingMore } = useRealtimeReservations(
    today,
    filterDate,
    handleValidReservationUpdate,
    reservationTypes,
  );

  useEffect(() => {
    getMyEarningsToday();

    const date = getDateFromOffset(user.companyUtcMinutesOffset);
    const firstMonthDate = format(startOfMonth(date), 'yyyy-MM-dd');
    const lastMonthDate = format(endOfMonth(date), 'yyyy-MM-dd');

    getEarnings(firstMonthDate, lastMonthDate);
  }, [jwt]);

  useEffect(() => {
    const storedFilterDate = localStorage.getItem('filterLead');
    if (storedFilterDate) {
      setFilterDate(storedFilterDate);
    }
  }, []);

  const isEmptyActivity = reservations?.length === 0 || undefined;

  useEffect(() => {
    if (!localStorage.getItem('filterLead')) localStorage.setItem('filterLead', 'CREATED_DATE');
  }, []);

  const handleSelectChange = (selectedOption) => {
    localStorage.setItem('filterLead', selectedOption);
    setFilterDate(selectedOption);
    setSelectedOption(selectedOption);
  };

  const renderWithdrawalMethodWarningBox = () => {
    if (
      (kycStatus !== 'APPROVED' || advantageNetworkStatus !== 'APPROVED') &&
      !userWithdrawalMethod
    ) {
      return null;
    }

    if (userWithdrawalMethod && userWithdrawalMethod.isActive) {
      return null;
    }

    let title = '';
    let subtitle = '';
    let nextUrl = '';

    if (user.withdrawalMethod === 'STRIPE') {
      if (userWithdrawalMethod?.status === 'WAITING_FIRST_PAYMENT') {
        return null;
      }
      else if (userWithdrawalMethod == null || userWithdrawalMethod.status === 'WAITING_USER_CREATION') {
        // user needs to create its connected account
        title = t('noAccountAssociated.STRIPE.title');
        subtitle = t('noAccountAssociated.STRIPE.subtitle');
        nextUrl = '/add-personal-information';
      } 
      else if (userWithdrawalMethod?.status === 'PENDING_TOS_ACCEPTANCE') {
        // user needs to provide additional info for its connected account
        title = t('reviewConnectedAccountRequired.title');
        subtitle = t('reviewConnectedAccountRequired.subtitle');
        nextUrl = `/confirm-add-account/?id=${userWithdrawalMethod.userWithdrawalMethodId}`;
      }
      else if (userWithdrawalMethod?.status === 'PENDING_REVIEW') {
        title = t('accountBeingReviewed.title');
        subtitle = t('accountBeingReviewed.subtitle');
        nextUrl = null;
      }
    } else if (user.withdrawalMethod === 'MERCADOPAGO') {
      title = t('noAccountAssociated.mercadoPago.title');
      subtitle = t('noAccountAssociated.mercadoPago.subtitle');
      nextUrl = '/mercadopagoaccount';
    } else if (wireMethod) {
      title = t('noAccountAssociated.WIRE.title');
      subtitle = t('noAccountAssociated.WIRE.subtitle');
      if (user.withdrawalMethod === 'WIRE_INTERNATIONAL') {
        nextUrl = '/international-bank';
      } else if (user.withdrawalMethod === 'WIRE_MEXICO') {
        nextUrl = '/national-bank';
      }
    }

    return (
      <WarningBox
        title={title}
        subtitle={
          <div>
            {subtitle}
            {nextUrl && <span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
              {t('here')}
            </span>}
          </div>
        }
        onClick={() => history.push(nextUrl)}
      />
    );
  };

  return (
    <div>
      <AccountHeader title={app.name} icon={'config'} pathName='/configuration' />
      <ContentApp menu header>
        {!wireMethod && renderWithdrawalMethodWarningBox()}
        <div className='mt-2'>
          <Balance
            title={t('earnings')}
            todayBalance={myEarningsToday?.amount}
            last7DaysBalance={monthEarning?.amount}
            showText={true}
            fontSize='1.8rem'
            showAllBalances={true}
            loading={loadingBalance}
            mpVerified={true}
            showArrow={true}
          />
        </div>

        <div className='d-flex mt-2'>
          {app.hasMarketingCampaign ? (
            <>
              <div className='d-flex flex-column' style={{ width: '50%' }}>
                <CustomButton
                  size='link'
                  text={t('campaigns')}
                  leftIcon={<InstagramIcon2 size={20} fillColor='#000' />}
                  rightIcon={<ArrowIcon size={15} />}
                  onClick={() => history.push('/campaigns-feed')}
                  className='mb-2'
                />

                <CustomButton
                  rightIcon={<ArrowIcon size={15} />}
                  leftIcon={<QrIconHome size={20} fillColor='#000' />}
                  text={t(' QR')}
                  onClick={() => history.push('/qr-restaurant')}
                  size='link'
                />
              </div>

              <div className='w-50 ml-2'>
                <CustomButton
                  size='reserva'
                  rightIcon={<ArrowIcon size={15} />}
                  leftIcon={<PaymentLink size={25} fillColor='#000' />}
                  text={t('Referencias')}
                  onClick={() => history.push('/restaurant-list')}
                  fontSize='24px'
                />
              </div>
            </>
          ) : (
            <Button
              size='large'
              className='col btn-primary'
              text={t('bookEarnProfits')}
              icon={true}
              typeIcon='arrow'
              iconColor='#000'
              onClick={() => history.push('/restaurant-list')}
            />
          )}
        </div>

        <div className='bold font-normal text-lighter-gray mt-3'>{t('Mis deals')}</div>
        <div className='row mt-3 mb-4'>
          <div className='col pr-1'>
            <div
              className={`account-button ${selectedOption === 'ALL' ? 'selected' : ''}`}
              onClick={() => handleSelectChange('ALL')}
            >
              {t('all')}
            </div>
          </div>
          <div className='col col-md p-0'>
            <div
              className={`account-button ${selectedOption === 'CREATED_DATE' ? 'selected' : ''}`}
              onClick={() => handleSelectChange('CREATED_DATE')}
            >
              {t('mostRecent')}
            </div>
          </div>
          <div className='col pl-1'>
            <div
              className={`account-button ${selectedOption === 'PAID' ? 'selected' : ''}`}
              onClick={() => handleSelectChange('PAID')}
            >
              {t('paid')}
            </div>
          </div>
        </div>

        {loading ? (
          skeletonLoading()
        ) : (
          <>
            {isEmptyActivity ? (
              <EmptyActivity subtitle={<div className='bold'>{t('noLeads')}</div>} />
            ) : (
              <>
                {reservations?.map((r, index) => (
                  <div key={index}>
                    <LeadItem
                      activity={r}
                      date={r.createdDate}
                      rightText={r.commissionAmount}
                      index={index}
                      lastIndex={reservations.length - 1}
                      handleClick={() => history.push(`/lead-details?id=${r.id}`)}
                    />
                  </div>
                ))}
                {/* {loadingMore ? (
                  <div className='p-3 d-flex justify-content-center'>
                    <CircleSpinner
                      className='spinner-app mx-auto'
                      style={{ margin: 'auto' }}
                      size={40}
                      color='var(--color-font-primary)'
                    />
                  </div>
                ) : (
                  hasMore && (
                    <div
                      className='text-secondary font-small fw-semibold text-center p-2 mt-2'
                      style={{ border: '1px solid #3D3D3D', borderRadius: '5px' }}
                      onClick={() => history.push('/all-lead-links')}
                    >
                      {t('Mostrar todos los deals')}
                    </div>
                  )
                )} */}
              </>
            )}
          </>
        )}
        <NavSelector history={history} />
      </ContentApp>
    </div>
  );
};

export default Home;
